import type { InterruptResolveFn } from "@copilotkit/react-core/v2";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { COPILOT_ANSWER_FAILED } from "@/constants/copilot/copilot-constants";
import { invalidateAppointmentQueries } from "@/lib/appointments/invalidate-appointment-queries";
import type { ChangeSummary } from "@/types/copilot/copilot-types";

type Decision = "approved" | "declined";

interface PendingChange {
  id: string;
  metadata?: unknown;
}

export function ApprovalCards({ changes, resolve }: { changes: PendingChange[]; resolve: InterruptResolveFn }) {
  const queryClient = useQueryClient();
  // The answers so far (a ref, so the last answer sees every earlier one), mirrored to state for rendering.
  const answers = useRef<Record<string, Decision>>({});
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  const answer = async (id: string, decision: Decision, reason?: string) => {
    answers.current[id] = decision;
    setDecisions({ ...answers.current });
    const payload = decision === "approved" ? { approved: true } : { approved: false, ...(reason ? { reason } : {}) };
    try {
      const resumed = await resolve(payload, id);
      if (resumed && Object.values(answers.current).includes("approved")) invalidateAppointmentQueries(queryClient);
    } catch {
      delete answers.current[id];
      setDecisions({ ...answers.current });
      toast.error(COPILOT_ANSWER_FAILED);
    }
  };

  return (
    <div className="my-2 flex flex-col gap-2">
      {changes.map((change) => (
        <ApprovalCard
          key={change.id}
          summary={change.metadata as ChangeSummary}
          decision={decisions[change.id]}
          onApprove={() => answer(change.id, "approved")}
          onDecline={(reason) => answer(change.id, "declined", reason)}
        />
      ))}
    </div>
  );
}

function ApprovalCard({
  summary,
  decision,
  onApprove,
  onDecline,
}: {
  summary: ChangeSummary;
  decision?: Decision;
  onApprove: () => void;
  onDecline: (reason: string) => void;
}) {
  const [declining, setDeclining] = useState(false);
  // A single optional note for the agent - no validation, no API call - so local state, not a form.
  const [reason, setReason] = useState("");

  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-sm">{summary.title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 p-3 pt-0 text-xs">
        {summary.details.map((line) => (
          <p key={line}>{line}</p>
        ))}
        {decision ? (
          <p className="text-muted-foreground">{decision === "approved" ? "Approved" : "Declined"}</p>
        ) : declining ? (
          <>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why? (optional)"
              className="min-h-14 text-xs"
            />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onDecline(reason.trim())}>
                Decline
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDeclining(false)}>
                Back
              </Button>
            </div>
          </>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" onClick={onApprove}>
              Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDeclining(true)}>
              Decline
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
